import React from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useShippingMethods from "../../hooks/useShippingMethods";

/**
 * Admin page to manage shipping methods
 * @returns {JSX.Element} The ShippingMethodsPage component
 */
const ShippingMethodsPage = () => {
  const { t } = useTranslation("shipping");
  const {
    shippingMethods,
    isLoading,
    openDialog,
    openDeleteDialog,
    selectedMethod,
    formData,
    errors,
    page,
    rowsPerPage,
    handleInputChange,
    handleAddOpen,
    handleEditOpen,
    handleClose,
    handleDeleteOpen,
    handleSave,
    handleDelete,
    handleChangePage,
    handleChangeRowsPerPage,
    fetchShippingMethods,
  } = useShippingMethods();

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              {t("title")}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
            >
              {t("addMethod")}
            </Button>
            <IconButton
              onClick={fetchShippingMethods}
              sx={{ ml: 1 }}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 3, width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="shipping methods table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("columns.name")}</TableCell>
                  <TableCell>{t("columns.price")}</TableCell>
                  <TableCell>{t("columns.deliveryTime")}</TableCell>
                  <TableCell>{t("columns.freeShipping")}</TableCell>
                  <TableCell>
                    {t("columns.trackingUrlFormat", "Tracking URL")}
                  </TableCell>
                  <TableCell>{t("columns.active")}</TableCell>
                  <TableCell align="right">{t("common:actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && shippingMethods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={32} sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                ) : shippingMethods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t("noData")}
                    </TableCell>
                  </TableRow>
                ) : (
                  shippingMethods
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((method) => (
                      <TableRow key={method.id} hover>
                        <TableCell>
                          <Typography variant="body1">{method.name}</Typography>
                          {method.description && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: "block" }}
                            >
                              {method.description.length > 50
                                ? `${method.description.substring(0, 50)}...`
                                : method.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {Number(method.price).toFixed(2)} CHF
                        </TableCell>
                        <TableCell>
                          {method.estimatedDeliveryTime ||
                            t("common:notSpecified")}
                        </TableCell>
                        <TableCell>
                          {method.freeShippingThreshold ? (
                            <Chip
                              label={`${t("freeOver")} $${Number(
                                method.freeShippingThreshold
                              ).toFixed(2)}`}
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            t("common:no")
                          )}
                        </TableCell>
                        <TableCell>
                          {method.trackingUrlFormat ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {method.trackingUrlFormat}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t("columns.noTracking", "Not configured")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              method.isActive
                                ? t("common:active")
                                : t("common:inactive")
                            }
                            color={method.isActive ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t("common:edit")}>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditOpen(method)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("common:delete")}>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteOpen(method)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={shippingMethods.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMethod ? t("editMethod") : t("addMethod")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label={t("fields.name")}
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="price"
                label={t("fields.price")}
                value={formData.price}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="number"
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.price}
                helperText={errors.price}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t("fields.description")}
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="estimatedDeliveryTime"
                label={t("fields.deliveryTime")}
                value={formData.estimatedDeliveryTime}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                placeholder="e.g., 3-5 business days"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="freeShippingThreshold"
                label={t("fields.freeShippingThreshold")}
                value={formData.freeShippingThreshold}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="number"
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.freeShippingThreshold}
                helperText={errors.freeShippingThreshold}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="minOrderAmount"
                label={t("fields.minOrderAmount")}
                value={formData.minOrderAmount}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="number"
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.minOrderAmount}
                helperText={errors.minOrderAmount}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="maxOrderAmount"
                label={t("fields.maxOrderAmount")}
                value={formData.maxOrderAmount}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="number"
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                error={!!errors.maxOrderAmount}
                helperText={errors.maxOrderAmount}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label={t("fields.active")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="trackingUrlFormat"
                name="trackingUrlFormat"
                label={t(
                  "admin.shippingMethods.trackingUrlFormat",
                  "Tracking URL Format"
                )}
                value={formData.trackingUrlFormat || ""}
                onChange={handleInputChange}
                error={!!errors.trackingUrlFormat}
                helperText={
                  errors.trackingUrlFormat ||
                  t(
                    "admin.shippingMethods.trackingUrlFormatHelp",
                    "Use {trackingNumber} as placeholder for tracking number (e.g., https://track.carrier.com/{trackingNumber})"
                  )
                }
                variant="outlined"
                placeholder="https://track.carrier.com/{trackingNumber}"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {t("common:save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleClose}>
        <DialogTitle>{t("deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deleteConfirmation", { name: selectedMethod?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {t("common:delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShippingMethodsPage;
